import React from 'react';
import { StatusBar, StatusSelect, LOAD_STATUSES, loadStatusColor, deriveLoadStatus } from './StatusBar';
import { useAuth } from '../../context/AuthContext';
import { NotesRow } from './NotesRow';
import { SearchableSelect } from '../ui/searchable-select';

const EQUIPMENT_TYPES = [
  'Dry Van', 'Refrigerated Trailer', 'Flatbed', 'Step Deck', 'Conestoga', 'Power Only',
  'Straight Truck', 'Box Truck', 'Sprinter Van', 'Hotshot', 'Double Drop', 'Removable Gooseneck',
  'Lowboy', 'Tanker', 'Hopper Bottom', 'Dump Trailer', 'Auto Carrier', 'Container', 'Chassis',
  'Drayage', 'Curtain Side', 'Livestock Trailer', 'Logging Trailer', 'Pneumatic Trailer',
  'Bulk Tanker', 'Food-Grade Tanker', 'Cryogenic Tanker', 'Container Chassis',
  '20-Foot Container', '40-Foot Container',
];

export const LoadInfoPanel = ({ load, onChange, onDispatch, onSave, onDuplicate, onShare, onDelete, onUpdates, onDocuments, isDirty, lastNote, onOpenNotes, onSaveNote }) => {
  const { isAdmin } = useAuth();
  const derived = deriveLoadStatus(load);
  const statusOptions = isAdmin() ? LOAD_STATUSES
    : load.status === 'Released' ? ['Delivered', 'Released']
    : derived === 'Delivered' ? ['Delivered', 'Released'] : [load.status || derived];
  const statusTitle = isAdmin() ? 'Status updates automatically; admins may override'
    : 'Load Status updates automatically from carrier assignment and arrival/departure times. Only Delivered → Released is set by hand.';
  const set = (field) => (e) => onChange(field, e.target.value);
  const selectOnFocus = (e) => { e.target.select(); e.target.dataset.justFocused = '1'; };
  const keepSelection = (e) => {
    if (e.target.dataset.justFocused === '1') { e.preventDefault(); delete e.target.dataset.justFocused; }
  };

  return (
    <div className="lw-panel" data-testid="lw-panel-load-info">
      <StatusBar
        left={<span title={statusTitle}>Load Info{!isAdmin() && statusOptions.length === 1 ? <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 6 }}>(auto)</span> : null}</span>}
        right={
          <span title={statusTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {load.activity?.label && <span style={{ fontSize: 11, fontWeight: 700 }} data-testid="lw-activity-label">{load.activity.label}</span>}
            {load.status === 'Booked' && load.carrier_id && load.id && onDispatch && (
              <button className="lw-btn" style={{ height: 20, padding: '0 8px', fontSize: 11 }} onClick={onDispatch} title="Records the dispatch time and user" data-testid="lw-btn-dispatch">Mark Dispatched</button>
            )}
            <StatusSelect
              value={load.status}
              options={statusOptions}
              onChange={(v) => onChange('status', v)}
              disabled={!isAdmin() && statusOptions.length === 1}
              testId="lw-status-select"
            />
          </span>
        }
        color={loadStatusColor(load.status)}
        testId="lw-statusbar-load-info"
      />

      {load.activity?.flags?.length > 0 && (
        <div className="lw-row" style={{ fontSize: 11, color: '#7a4b00' }} data-testid="lw-activity-flags">⚑ {load.activity.flags.join(' · ')}</div>
      )}
      <div className="lw-row">
        <span className="lw-label w110">Customer Ref #</span>
        <input className="lw-input" value={load.customer_ref || ''} onChange={set('customer_ref')} data-testid="lw-customer-ref" data-field="customer_ref" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Commodity</span>
        <input className="lw-input" value={load.commodity || ''} onChange={set('commodity')} data-testid="lw-commodity" data-field="commodity" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Equipment</span>
        <SearchableSelect
          options={(load.equipment && !EQUIPMENT_TYPES.includes(load.equipment)
            ? [load.equipment, ...EQUIPMENT_TYPES]
            : EQUIPMENT_TYPES
          ).map(e => ({ value: e, label: e }))}
          value={load.equipment || ''}
          onValueChange={(val) => onChange('equipment', val)}
          placeholder="Type to search equipment..."
          className="h-7 text-sm flex-1"
          data-testid="lw-equipment"
        />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Temperature</span>
        <input className="lw-input narrow" value={load.temperature || ''} onChange={set('temperature')} data-testid="lw-temperature" />
        <span className="lw-label" style={{ marginLeft: 8 }}>Reefer</span>
        <input className="lw-input narrow" value={load.reefer_mode || ''} onChange={set('reefer_mode')} placeholder="Mode" data-testid="lw-reefer-mode" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Weight</span>
        <input className="lw-input narrow" value={load.weight || ''} onChange={set('weight')} data-testid="lw-weight" data-field="weight" />
        <span className="lw-label" style={{ marginLeft: 8 }}>Pallets</span>
        <input className="lw-input tiny" value={load.pallet_count || ''} onChange={set('pallet_count')} data-testid="lw-pallets" data-field="pallet_count" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Customer Miles</span>
        <input className="lw-input narrow" value={load.customer_mileage || ''} onChange={set('customer_mileage')} data-testid="lw-customer-miles" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Rate to Cover</span>
        <input type="number" step="0.01" className="lw-input narrow" placeholder="Min" value={load.rate_to_cover_min ?? ''} onChange={set('rate_to_cover_min')} onFocus={selectOnFocus} onMouseUp={keepSelection} data-testid="lw-rate-cover-min" />
        <input type="number" step="0.01" className="lw-input narrow" placeholder="Max" value={load.rate_to_cover_max ?? ''} onChange={set('rate_to_cover_max')} onFocus={selectOnFocus} onMouseUp={keepSelection} data-testid="lw-rate-cover-max" />
      </div>
      <div className="lw-row">
        <span className="lw-label w110">Cross Border</span>
        <input
          type="checkbox"
          className="lw-checkbox"
          checked={!!load.cross_border}
          onChange={(e) => onChange('cross_border', e.target.checked)}
          data-testid="lw-cross-border-checkbox"
        />
        <span className="lw-label" style={{ marginLeft: 16 }}>Tracking</span>
        <input
          type="checkbox"
          className="lw-checkbox"
          checked={!!load.tracking}
          onChange={(e) => onChange('tracking', e.target.checked)}
          data-testid="lw-tracking-checkbox"
        />
      </div>

      <div className="lw-sep" />
      <NotesRow lastNote={lastNote} onOpenNotes={onOpenNotes} onSaveNote={onSaveNote} testId="lw-notes-load" />

      <div className="lw-btnrow">
        <button className="lw-btn" onClick={onDuplicate} data-testid="lw-btn-duplicate">Duplicate</button>
        <button className="lw-btn" onClick={onShare} data-testid="lw-btn-share">Share</button>
        <button
          className="lw-btn"
          onClick={onSave}
          disabled={!isDirty}
          style={isDirty
            ? { background: 'var(--lw-red)', borderColor: 'var(--lw-red)', color: '#fff' }
            : { background: '#e9ebf1', borderColor: 'var(--lw-border)', color: '#9aa1b0', cursor: 'default' }}
          data-testid="lw-btn-save"
        >
          {isDirty ? 'Save *' : 'Saved'}
        </button>
        <button className="lw-btn" onClick={onUpdates} data-testid="lw-btn-updates">Updates</button>
        <button className="lw-btn" onClick={onDocuments} data-testid="lw-btn-load-documents">
          Documents{(load.load_documents || []).length > 0 ? ` (${load.load_documents.length})` : ''}
        </button>
        <button className="lw-btn" style={{ color: 'var(--lw-red)', borderColor: 'var(--lw-red)' }} onClick={onDelete} data-testid="lw-btn-delete">Delete</button>
      </div>
    </div>
  );
};
